import { useEffect, useState } from "react";
import { Loader2, TrendingUp } from "lucide-react";
import { api } from "../App";

/* HostEarningsTable
 * -----------------
 * Drop-in card that reads `/api/stripe/host/earnings` and shows the host
 * their pending / completed / refunded payout totals plus a recent-bookings
 * table. Pulls live data, refreshes when remounted.
 */
const fmtMxn = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Intl.DateTimeFormat("es-MX", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(s));
  } catch (_e) {
    return s;
  }
};

const StatusBadge = ({ paymentStatus, payoutStatus }) => {
  if (payoutStatus === "reversed") {
    return <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-800 text-[11px] font-medium ring-1 ring-rose-200">Reembolsado</span>;
  }
  if (paymentStatus === "paid") {
    return <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 text-[11px] font-medium ring-1 ring-emerald-200">Pagado</span>;
  }
  return <span className="px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700 text-[11px] font-medium">{paymentStatus || "—"}</span>;
};

export default function HostEarningsTable() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      api.get("/stripe/host/earnings")
        .then(({ data }) => { if (!cancelled) setData(data); })
        .catch(() => { /* silent */ })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  if (loading) {
    return (
      <div className="card-base mt-6 flex items-center justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }
  if (!data) return null;

  const items = data.items || [];
  const t = data.totals_mxn || {};

  return (
    <div className="card-base mt-6" data-testid="host-earnings-table">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
          <TrendingUp className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-lg text-agave">Mis ingresos</h3>
          <p className="text-sm text-neutral-600">Resumen de cada reserva pagada por tus huéspedes.</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="rounded-xl bg-sand-light p-3 ring-1 ring-black/5">
          <div className="text-[11px] text-neutral-500 uppercase tracking-wide">Pendiente</div>
          <div className="text-xl font-semibold text-agave mt-0.5" data-testid="host-earnings-pending">{fmtMxn(t.pending)}</div>
        </div>
        <div className="rounded-xl bg-sand-light p-3 ring-1 ring-black/5">
          <div className="text-[11px] text-neutral-500 uppercase tracking-wide">Pagados</div>
          <div className="text-xl font-semibold text-emerald-700 mt-0.5" data-testid="host-earnings-completed">{fmtMxn(t.completed)}</div>
        </div>
        <div className="rounded-xl bg-sand-light p-3 ring-1 ring-black/5">
          <div className="text-[11px] text-neutral-500 uppercase tracking-wide">Reembolsados</div>
          <div className="text-xl font-semibold text-rose-700 mt-0.5">{fmtMxn(t.refunded)}</div>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-neutral-500 text-center py-6">
          Aún no tienes reservas pagadas. Cuando un huésped pague una renta, verás aquí su monto.
        </p>
      ) : (
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] text-neutral-500 uppercase tracking-wide text-left">
                <th className="py-2 font-medium">Reserva</th>
                <th className="py-2 font-medium">Fechas</th>
                <th className="py-2 font-medium text-right">Total</th>
                <th className="py-2 font-medium text-right">Tu pago (85%)</th>
                <th className="py-2 font-medium text-right">Estado</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.booking_id} className="border-t border-neutral-100">
                  <td className="py-2.5 font-mono text-[12px] text-neutral-700">{b.booking_id?.slice(0, 12)}…</td>
                  <td className="py-2.5 text-neutral-600">{fmtDate(b.start_date)} → {fmtDate(b.end_date)}</td>
                  <td className="py-2.5 text-right">{fmtMxn(b.total_price)}</td>
                  <td className="py-2.5 text-right font-medium text-agave">{fmtMxn(b.payout_mxn)}</td>
                  <td className="py-2.5 text-right">
                    <StatusBadge paymentStatus={b.payment_status} payoutStatus={b.payout_status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
